import React, { useState, useEffect } from 'react';

import Representative from '~/components/ProjectSections/Representative';
import Client from '~/components/ProjectSections/Client';
import ClientPatterns from '~/components/ProjectSections/ClientPatterns';
import Main from '~/components/ProjectSections/Main';
import ClientContact from '~/components/ProjectSections/ClientContact';
import Records from '~/components/ProjectSections/Records';
import Files from '~/components/ProjectSections/Files';
import RequestFiles from '~/components/ProjectSections/RequestFiles';
import CustomProjectFiles from '~/components/ProjectSections/CustomProjectFiles';
import Prices from '~/components/ProjectSections/Prices';
import Tplp from '~/components/ProjectSections/Tplp';
import Labels from '~/components/ProjectSections/Labels';
import CompletionFiles from '~/components/ProjectSections/CompletionFiles';

import api from '~/services/api';

interface IDisplayProps {
  project_id: string;
  all?: boolean;
}

interface IProject {
  id: string;
  type: string;
  code: string;
  unit_price: number;
  price: number;
  shipping_price: number;
  total_price: number;
  rep_as_client: boolean;
  erp_client_code: string;
  erp_rep_code: string;
  item_amount: number;
  note: string;
  client_contact_name: string;
  client_contact_phone: string;
  client_contact_email: string;
  custom_project: boolean;
  note_custom_project: string;
  project_procedure_id: string;
  display: {
    code: string;
    name: string;
  };
  project_procedure: {
    id: string;
    sequence: number;
  };
  third_party_logistics_provider: {
    name: string;
    email: string;
  };
}

const Display: React.FC<IDisplayProps> = ({ project_id, all }) => {
  const [project, setProject] = useState<IProject>();

  useEffect(() => {
    api.get(`/projects/id?id=${project_id}`).then(response => {
      setProject(response.data);
    });
  }, [project_id]);

  function handleRepresentative(): React.ReactNode | null {
    if (project) {
      return (
        <Representative
          erp_rep_code={project.erp_rep_code ? project.erp_rep_code : ''}
        />
      );
    }

    return null;
  }

  function handleClient(): React.ReactNode | null {
    if (project) {
      return (
        <Client
          rep_as_client={project.rep_as_client}
          erp_client_code={
            project.erp_client_code ? project.erp_client_code : ''
          }
          erp_rep_code={project.erp_rep_code ? project.erp_rep_code : ''}
        />
      );
    }

    return null;
  }

  function handleClientPatterns(): React.ReactNode | null {
    if (project) {
      return (
        <ClientPatterns
          erp_client_code={
            project.erp_client_code ? project.erp_client_code : ''
          }
        />
      );
    }

    return null;
  }

  function handleMain(): React.ReactNode | null {
    if (project) {
      if (project.project_procedure.sequence > 1 || all) {
        return (
          <Main
            code={project.code ? project.code : ''}
            item={
              project.display.code && project.display.name
                ? `(${project.display.code}) - ${project.display.name}`
                : ''
            }
            note={project.note ? project.note : ''}
            custom_project={project.custom_project}
            note_custom_project={project.note_custom_project}
          />
        );
      }
    }

    return null;
  }

  function handleClientContact(): React.ReactNode | null {
    if (project) {
      if (project.project_procedure.sequence > 1 || all) {
        return (
          <ClientContact
            client_contact_name={
              project.client_contact_name ? project.client_contact_name : ''
            }
            client_contact_phone={
              project.client_contact_phone ? project.client_contact_phone : ''
            }
            client_contact_email={
              project.client_contact_email ? project.client_contact_email : ''
            }
          />
        );
      }
    }

    return null;
  }

  function handleRecords(): React.ReactNode | null {
    if (project) {
      if (project.project_procedure.sequence > 1 || all) {
        return <Records project_id={project.id} />;
      }
    }

    return null;
  }

  function handleFiles(): React.ReactNode | null {
    if (project) {
      if (project.project_procedure.sequence > 1 || all) {
        return (
          <Files
            project_id={project.id}
            project_procedure_id={project.project_procedure.id}
          />
        );
      }
    }

    return null;
  }

  function handleRequestFiles(): React.ReactNode | null {
    if (project) {
      if (project.project_procedure.sequence > 1 || all) {
        return (
          <RequestFiles
            project_id={project.id}
            project_procedure_id={project.project_procedure.id}
          />
        );
      }
    }

    return null;
  }

  function handleCustomProjectFiles(): React.ReactNode | null {
    if (project) {
      if (
        (project.project_procedure.sequence > 2 || all) &&
        project.custom_project
      ) {
        return (
          <CustomProjectFiles
            project_id={project.id}
            project_procedure_id={project.project_procedure.id}
          />
        );
      }
    }

    return null;
  }

  function handlePrices(): React.ReactNode | null {
    if (project) {
      if (project.project_procedure.sequence > 1 || all) {
        return (
          <Prices
            unit_price={project.unit_price}
            item_amount={project.item_amount}
            price={project.price}
            shipping_price={project.shipping_price}
            total_price={project.total_price}
          />
        );
      }
    }

    return null;
  }

  function handleTplp(): React.ReactNode | null {
    if (project) {
      if (project.project_procedure.sequence > 4 || all) {
        return (
          <Tplp
            tplp_name={project.third_party_logistics_provider?.name}
            tplp_email={project.third_party_logistics_provider?.email}
          />
        );
      }
    }

    return null;
  }

  function handleLabels(): React.ReactNode | null {
    if (project) {
      if (project.project_procedure.sequence > 7 || all) {
        return <Labels project_id={project.id} />;
      }
    }

    return null;
  }

  function handleCompletionFiles(): React.ReactNode | null {
    if (project) {
      if (project.project_procedure.sequence > 7 || all) {
        return (
          <CompletionFiles
            project_id={project.id}
            project_procedure_id={project.project_procedure.id}
          />
        );
      }
    }

    return null;
  }

  return (
    <>
      {handleRepresentative()}
      {handleClient()}
      {handleClientPatterns()}
      {handleMain()}
      {handleClientContact()}
      {handleRecords()}
      {handleFiles()}
      {handleRequestFiles()}
      {handleCustomProjectFiles()}
      {handlePrices()}
      {handleTplp()}
      {handleLabels()}
      {handleCompletionFiles()}
    </>
  );
};

export default Display;
